/**
 * RADIX SELECT COMPONENT
 * Composant Select basé sur Radix UI avec support pour iframe (portal={false})
 * Utilisé dans les formulaires publics pour éviter les problèmes de positionnement
 */

import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface RadixSelectOption {
  value: string;
  label: string;
}

export interface RadixSelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  /**
   * Options à afficher dans le select
   */
  options: RadixSelectOption[];

  /**
   * Placeholder affiché quand aucune option n'est sélectionnée
   */
  placeholder?: string;

  /**
   * Valeur sélectionnée
   */
  value?: string;

  /**
   * Callback appelé quand la valeur change
   */
  onChange?: (value: string) => void;

  /**
   * Si true, désactive le portal (utile pour les iframes)
   * @default true pour éviter les problèmes de positionnement dans les iframes
   */
  disablePortal?: boolean;
}

export const RadixSelect = forwardRef<HTMLButtonElement, RadixSelectProps>(
  (
    {
      className,
      options,
      placeholder = 'Sélectionnez une option',
      value,
      onChange,
      disabled,
      disablePortal = true,
      ...props
    },
    ref
  ) => {
    return (
      <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
        <SelectPrimitive.Trigger
          ref={ref}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm',
            'placeholder:text-zinc-400',
            'focus:outline-none focus:ring-2 focus:ring-zinc-900',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[placeholder]:text-zinc-400',
            className
          )}
          {...(props as any)}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon asChild>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal container={disablePortal ? null : undefined}>
          <SelectPrimitive.Content
            className={cn(
              'relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border border-zinc-200 bg-white text-zinc-950 shadow-md',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
            )}
            position="popper"
            sideOffset={5}
          >
            <SelectPrimitive.Viewport
              className={cn(
                'p-1',
                'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
              )}
            >
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none',
                    'focus:bg-zinc-100 focus:text-zinc-900',
                    'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                  )}
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <SelectPrimitive.ItemIndicator>
                      <Check className="h-4 w-4" />
                    </SelectPrimitive.ItemIndicator>
                  </span>
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    );
  }
);

RadixSelect.displayName = 'RadixSelect';
